// src/pages/Todo/TodoHistory.tsx — past completions browser
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { format, subDays } from "date-fns";
import { ArrowLeft, History as HistoryIcon, CheckCircle2 } from "lucide-react";
import todoApi, { type PlannerItem, type PersonalTodo } from "../../api/todo";
import TodoCard from "./TodoCard";
import { DateField } from "../../components/ui/DateField";

export default function TodoHistory() {
  const nav = useNavigate();
  const [from, setFrom] = useState(format(subDays(new Date(), 7), "yyyy-MM-dd"));
  const [to, setTo] = useState(format(new Date(), "yyyy-MM-dd"));
  const [items, setItems] = useState<PlannerItem[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await todoApi.history({ from, to });
      setItems([...(res.data.personal || []), ...(res.data.assigned || [])]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [from, to]);

  const grouped: Record<string, PlannerItem[]> = {};
  for (const t of items) {
    const dateStr = (t.completed_at || t.due_date || "").slice(0, 10) || "unknown";
    grouped[dateStr] = grouped[dateStr] || [];
    grouped[dateStr].push(t);
  }
  const dates = Object.keys(grouped).filter((d) => d !== "unknown").sort((a, b) => b.localeCompare(a));

  const handleOpen = (it: PlannerItem) => {
    if (it.source === "assigned") nav(`/tasks/${it.id}`);
  };

  return (
    <div className="gt-shell min-h-screen">
      <div className="max-w-4xl mx-auto p-4 md:p-7 space-y-6 gt-fade-in">
        <button onClick={() => nav(-1)} className="gt-btn is-ghost is-sm">
          <ArrowLeft className="w-4 h-4" /> Back to planner
        </button>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          textAlign: 'left',
          paddingBottom: 18,
          width: '100%',
        }}>
          <div className="gt-eyebrow-row">
            <span className="gt-leaf" />
            <span className="gt-eyebrow">i. Archive</span>
          </div>
          <h1 className="gt-page-title"><em>History</em></h1>
          <p className="gt-page-sub" style={{ marginTop: 6 }}>Review what got <em className="gt-serif-italic">done</em>.</p>
        </div>

        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <span className="gt-field-label">From</span>
            <div className="w-[200px]">
              <DateField value={from} onChange={setFrom} />
            </div>
          </div>
          <div>
            <span className="gt-field-label">To</span>
            <div className="w-[200px]">
              <DateField value={to} onChange={setTo} />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="gt-loading-ring" /></div>
        ) : dates.length === 0 ? (
          <div className="gt-card p-10">
            <div className="gt-empty">
              <div className="gt-empty-leaf"><CheckCircle2 className="w-6 h-6" /></div>
              <div className="gt-empty-title">All caught up</div>
              <div className="gt-page-sub">No completed tasks in this <em className="gt-serif-italic">range</em>.</div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {dates.map((d, i) => (
              <motion.div
                key={d}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="gt-eyebrow gt-numerals">{format(new Date(d + "T00:00:00"), "EEEE")}</span>
                  <span className="gt-rule" style={{ flex: 1 }} />
                  <span className="gt-page-sub gt-numerals">{format(new Date(d + "T00:00:00"), "MMM d, yyyy")}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                  {grouped[d].map((t) => (
                    <TodoCard
                      key={t.id}
                      item={t}
                      onToggle={() => {}}
                      onOpen={handleOpen}
                    />
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
